import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { NumberInput } from 'src/design/atoms/NumberInput';
import { Table } from 'src/design/molecules/Table';
import { formatRate } from 'src/utils';
import React from 'react';
import { JobClassificationRateRequestDto } from 'src/api/FiretrolApiClient';
import { useClassifications } from 'src/api/queries/organization/useClassifications';

export interface IClassificationRatesOverrideProps {
  value: Omit<JobClassificationRateRequestDto, 'jobId'>[];
  onChange: (value: Omit<JobClassificationRateRequestDto, 'jobId'>[]) => void;
}

export interface IClassificationRateOverride {
  classificationId: string;
  classificationName: string;
  scaleRate?: number;
  fringeRate?: number;
}

export const ClassificationRatesOverride: React.FC<
  IClassificationRatesOverrideProps
> = ({ value, onChange }) => {
  const { data: classifications, isLoading: loading } = useClassifications();

  const allClassifications: IClassificationRateOverride[] =
    React.useMemo(() => {
      return (classifications ?? []).map((classification) => {
        const existingOverride = value.find(
          (v) => v.classificationId === classification.id,
        );
        return {
          classificationId: classification.id,
          classificationName: classification.name,
          scaleRate: existingOverride?.scaleRate,
          fringeRate: existingOverride?.fringeRate,
        };
      });
    }, [classifications, value]);

  const updateOverride = (
    classificationId: string,
    field: 'scaleRate' | 'fringeRate',
    fieldValue: number | undefined,
  ) => {
    const existingIndex = value.findIndex(
      (v) => v.classificationId === classificationId,
    );

    if (existingIndex >= 0) {
      const updated = [...value];
      updated[existingIndex] = {
        ...updated[existingIndex],
        [field]: fieldValue,
      };

      const hasAnyValue =
        updated[existingIndex].scaleRate !== undefined ||
        updated[existingIndex].fringeRate !== undefined;

      if (!hasAnyValue) {
        updated.splice(existingIndex, 1);
      }

      onChange(updated);
    } else if (fieldValue !== undefined && fieldValue !== null) {
      onChange([
        ...value,
        {
          classificationId,
          scaleRate: field === 'scaleRate' ? fieldValue : 0,
          fringeRate: field === 'fringeRate' ? fieldValue : 0,
        },
      ]);
    }
  };

  if (loading) {
    return <Text>Loading classifications...</Text>;
  }

  if (allClassifications.length === 0) {
    return (
      <Text c="dimmed">
        No classifications available. Please create classifications in Settings
        first.
      </Text>
    );
  }

  return (
    <Stack gap="md">
      <Text size="sm" c="bright">
        <strong>Required:</strong> Set scale pay rates for each classification.
        Scale rate is the base hourly rate, and fringe rate includes all
        benefits. Both values are required for each classification.
      </Text>

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Classification</Table.Th>
            <Table.Th>
              <Group gap="xs">
                Scale Rate{' '}
                <Text size="xs" c="red">
                  *
                </Text>
              </Group>
            </Table.Th>
            <Table.Th>
              <Group gap="xs">
                Fringe Rate{' '}
                <Text size="xs" c="red">
                  *
                </Text>
              </Group>
            </Table.Th>
            <Table.Th>Total Rate</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {allClassifications.map((classification) => {
            const totalRate =
              (Number(classification.scaleRate) || 0) +
              (Number(classification.fringeRate) || 0);
            const hasValues =
              classification.scaleRate !== undefined ||
              classification.fringeRate !== undefined;

            return (
              <Table.Tr key={classification.classificationId}>
                <Table.Td>{classification.classificationName}</Table.Td>
                <Table.Td>
                  <NumberInput
                    value={classification.scaleRate}
                    onChange={(val) =>
                      updateOverride(
                        classification.classificationId,
                        'scaleRate',
                        val as number | undefined,
                      )
                    }
                    placeholder="Required"
                    prefix="$"
                    decimalScale={3}
                    min={0}
                    size="xs"
                  />
                </Table.Td>
                <Table.Td>
                  <NumberInput
                    value={classification.fringeRate}
                    onChange={(val) =>
                      updateOverride(
                        classification.classificationId,
                        'fringeRate',
                        val as number | undefined,
                      )
                    }
                    placeholder="Required"
                    prefix="$"
                    decimalScale={3}
                    min={0}
                    size="xs"
                  />
                </Table.Td>
                <Table.Td>{hasValues ? formatRate(totalRate) : '—'}</Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Stack>
  );
};
